import { takeEvery, takeLatest, fork, put, call, select, delay, take } from 'redux-saga/effects';
import { Contact, ExportStatus, UnsubReason, SuppressedStatus, ContactsOrderBy, ContactStatus } from '@dev/ee-api/ee-api-models';
import { stringify } from 'query-string';
import { omit, isEmpty, filter, flatten, without } from 'lodash-es';

import { push } from 'redux-first-history';

import {
    LoadSuppressions,
    StartLoadingSuppressions,
    SuppressionsLoaded,
    SuppressionsLoadingError,
    AddSuppressionStart,
    AddSuppressionError,
    AddSuppressionSuccess,
    DeleteSuppressionError,
    DeleteSuppressionStart,
    DeleteSuppressionSuccess,
    ExportSuppressionError,
    ExportSuppressionStart,
    ExportSuppressionSuccess,
    GetContactsPatternStart,
    GetContactsPatternError,
    GetContactsPatternSuccess,
    GetContactsUnsubscribedOverviewSuccess,
    GetContactsUnsubscribedOverviewError,
    GetContactsUnsubscribedSuccess,
    GetContactsUnsubscribedError,
    GetContactsPattern,
    getSuppressionsTotalCountStore,
} from '@api/store/suppressions/actions';
import {
    LoadSuppressionsAction,
    AddSuppressionAction,
    FindSuppressionsAction,
    DeleteSuppressionAction,
    ExportSuppressionAction,
    OrderByOptions,
    SuppressionsState,
    SuppressionsActionTypes,
    GetContactsUnsubscribedOverviewAction,
    SuppressionsContactsUnsubscribedPaginationInterface,
    GetContactsUnsubscribedAction,
    ExportContactsUnsubscribedAction,
    defaultSuppressedStatuses,
} from '@api/store/suppressions/types';
import {
    getSuppressions,
    addSuppression,
    deleteSuppressions,
    GetSuppressionsParams,
    exportSuppressions,
    getContactsPattern,
    uploadSuppressions,
    deleteFile,
    checkTaskStatus,
    getContactsUnsubscribedOverview,
    getContactsUnsubscribed,
    exportContactsUnsubscribed,
} from '@api/services/ElasticApi';

import { ShowToast, HideToast } from '@api/store/toast/actions';
import { ApplicationState as State, getRouterStore } from '@api/store';
import { UploadState } from '@api/store/upload/types';
import { calculateActivePage, calculateOffset, parseUrlParams } from '@api/services/tools/handleUrlParams';
import dayjs from '@api/services/tools/dayjs';
import { DEFAULT_PER_PAGE_COUNT, SHORT_DATE_FORMAT } from '@api/services/constants';
import { extractUnsubscribedContactsStatusCounts } from '@api/services/tools/extractUnsubscribedContactsStatusCount';
import i18n from '@api/i18n';
import { ExportsPaths, SuppressionsPaths } from '@api/services/dictionaries/paths';
import { UploadFiles } from '@api/store/upload/actions';
import { ShowDialog } from '@api/store/dialog/actions';
import { forEach, find } from 'lodash';
import { Logger } from '../services/errors/appLogger';

const SEARCH_FOR_CONTACT = SuppressionsActionTypes.SEARCH_FOR_CONTACT;
const MAX_PER_PAGE = 25;

const deleteSuppressionAllStatuses = [
    ...without(Object.values(SuppressedStatus), SuppressedStatus.Complained),
    ContactStatus.Abuse,
] as SuppressedStatus[];

const getSuppersionContactsRule = (statuses = Object.values(SuppressedStatus)): string => `status = ${statuses.join(' OR status = ')}`;

const getSuppressionsState = (state: State): SuppressionsState => state.suppressions;
const getUploadState = (state: State): UploadState => state.upload;
const getContacts = (state: State): Contact[] => state.suppressions.contacts;
const getStatuses = (state: State): SuppressedStatus[] | ContactStatus[] => state.suppressions.statuses;
const getActivePage = (state: State): number => state.suppressions.activePage;
const getOrderBy = (state: State): ContactsOrderBy => state.suppressions.orderBy;
const getPerPage = (state: State): number => state.suppressions.perPage;
const getTotalCount = (state: State): number => state.suppressions.counts.totalCount;

const getOffset = (activePage, limit): { offset: number } => {
    const offset = limit * activePage - limit;

    return { offset };
};

const getCallParams = (
    page: number,
    statuses?: SuppressedStatus[],
    search?: string,
    orderBy?: ContactsOrderBy,
    perPage?: number
): GetSuppressionsParams => {
    const { offset } = getOffset(page, perPage);
    let queryParams = parseUrlParams(window?.location?.search);

    if (queryParams.statuses) {
        queryParams.statuses = flatten([queryParams.statuses]);

        queryParams.statuses = filter(queryParams.statuses, (s: string): SuppressedStatus => {
            if (Object.values(SuppressedStatus).includes(s as SuppressedStatus)) {
                return s as SuppressedStatus;
            }
        }) as SuppressedStatus[];
    }

    if (queryParams.orderBy && !Object.values(ContactsOrderBy).includes(orderBy)) {
        queryParams = omit(queryParams, 'orderBy');
    }

    return {
        offset: parseInt(queryParams.offset as string) || offset,
        limit: parseInt(queryParams.perPage as string) || perPage,
        search: (queryParams.search as string) || search,
        statuses: !isEmpty(queryParams.statuses) ? (queryParams.statuses as SuppressedStatus[]) : defaultSuppressedStatuses,
        orderBy: !isEmpty(queryParams.orderBy) ? (queryParams.orderBy as ContactsOrderBy) : orderBy,
    };
};

function* handleError(e: any): any {
    let error = e;
    if (!e.response || !e.response.data) {
        Logger.error(error);
    } else {
        error = e.response.data.Error;
    }
    yield put(
        ShowToast({
            severity: 'error',
            message: `Error: ${error}`,
            duration: 6000,
            onClose: HideToast,
        })
    );
}

function* handleURLQuery(payload, type: string, activePage: number, orderBy: OrderByOptions, perPage: number): any {
    let queryParams = parseUrlParams(window?.location?.search);

    const { statuses = yield select(getStatuses) } = payload;

    if (type === SEARCH_FOR_CONTACT && payload.search) {
        queryParams.search = payload.search;
    }

    if (type === SEARCH_FOR_CONTACT && !payload.search) {
        queryParams = omit(queryParams, 'search');
    }

    if (orderBy && orderBy !== queryParams.orderBy) {
        queryParams.orderBy = orderBy;
    } else if (!orderBy && queryParams.orderBy) {
        queryParams = omit(queryParams, 'orderBy');
    }

    activePage < 2 ? (queryParams = omit(queryParams, 'page')) : (queryParams.page = activePage.toString());

    if (!isEmpty(statuses)) {
        queryParams.statuses = statuses;
    } else {
        queryParams = omit(queryParams, 'statuses');
    }

    const maxElements = parseInt(queryParams.perPage as string);

    if (maxElements !== perPage && perPage !== MAX_PER_PAGE) {
        queryParams.perPage = perPage.toString();
    }

    if (perPage === MAX_PER_PAGE) {
        queryParams = omit(queryParams, 'perPage');
    }

    yield put(push({ search: stringify(queryParams, { arrayFormat: 'comma' }) }));
}

function* loadContacts(action: LoadSuppressionsAction | FindSuppressionsAction): any {
    const { payload = {}, type } = action;
    const {
        activePage = yield select(getActivePage),
        statuses = yield select(getStatuses),
        orderBy = yield select(getOrderBy),
        perPage = yield select(getPerPage),
    } = payload;

    if (action.type === SEARCH_FOR_CONTACT) {
        yield delay(500);
    }

    yield handleURLQuery(payload, type, activePage, orderBy, perPage);
    const suppressionsParams = getCallParams(activePage, statuses, payload.search, orderBy, perPage);
    yield put(StartLoadingSuppressions());

    try {
        const resp = yield call(getSuppressions, suppressionsParams);

        const totalCount = parseInt(resp.headers['x-total-count']) || 0;
        const bouncedCount = parseInt(resp.headers['x-total-bounced-count']) || 0;
        const unsubscribedCount = parseInt(resp.headers['x-total-unsubscribed-count']) || 0;
        const abuseCount = parseInt(resp.headers['x-total-abuse-count']) || 0;
        const inactiveCount = parseInt(resp.headers['x-total-inactive-count']) || 0;
        const staleCount = parseInt(resp.headers['x-total-stale-count']) || 0;
        const transactionalCount = parseInt(resp.headers['x-total-transactional-count']) || 0;

        const counts = {
            totalCount,
            bouncedCount,
            unsubscribedCount,
            abuseCount,
            inactiveCount,
            staleCount,
            transactionalCount,
        };

        const contacts = resp.data;

        yield put(SuppressionsLoaded(contacts, counts));
    } catch (e) {
        yield put(SuppressionsLoadingError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );

        Logger.error(e.response.data.Error);
    }
}

function* createSuppression(action: AddSuppressionAction): any {
    const { payload } = action;

    yield put(AddSuppressionStart());

    try {
        const contacts = yield select(getContacts);

        if (payload.emails) {
            const suppressions = yield call(addSuppression, { Emails: payload.emails, Status: payload.status });
            contacts.concat(suppressions);
            yield put(AddSuppressionSuccess(contacts));
        } else {
            const suppresionState = yield select(getSuppressionsState);
            const { fileName, contactsPattern } = suppresionState;

            const pattern = contactsPattern.reduce((acc: any, elem: string) => {
                const header = elem.trim().toLowerCase();
                if (header === 'email' || header === 'e-mail' || header === 'mail') {
                    acc[elem] = 'email';
                    return acc;
                }

                return acc;
            }, {});

            yield call(uploadSuppressions, fileName, pattern, payload.status);
        }

        yield put(push(SuppressionsPaths.Suppressions));
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('suppressionsaddedpleasewait'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(AddSuppressionSuccess(contacts));
    } catch (e) {
        yield put(AddSuppressionError());
        yield handleError(e);
    }
}

function* deleteSuppression(action: DeleteSuppressionAction): any {
    const { payload } = action;
    const { location } = yield select(getRouterStore);
    const totalCount = yield select(getSuppressionsTotalCountStore);

    yield put(DeleteSuppressionStart());

    try {
        const queryParams = parseUrlParams(location.search);
        let activePage = queryParams.page || 1;
        let statuses = (queryParams.statuses as SuppressedStatus[]) ?? defaultSuppressedStatuses;
        let resp;
        if (payload.selectedAll) {
            resp = yield call(deleteSuppressions, { rule: getSuppersionContactsRule(deleteSuppressionAllStatuses) });
        } else {
            resp = yield call(deleteSuppressions, { emails: payload.emails });
        }
        const count = resp.data ?? 0;
        const countAfterDelete = totalCount - count;

        const perPage = queryParams.perPage ? +queryParams.perPage : DEFAULT_PER_PAGE_COUNT;
        if (totalCount && totalCount > count) {
            const offset = calculateOffset(+queryParams.page, perPage, countAfterDelete);
            activePage = calculateActivePage(offset, perPage, countAfterDelete);
        }
        if (totalCount === count) {
            statuses = [];
            activePage = 1;
        }

        yield put(DeleteSuppressionSuccess());
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('suppressionsdeleted', { count }),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(LoadSuppressions({ activePage, statuses }));
    } catch (e) {
        yield put(DeleteSuppressionError());
        yield handleError(e);
    }
}

function* exportSuppression(action: ExportSuppressionAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    yield put(ExportSuppressionStart());

    const { fileFormat, emails, compressionFormat, fileName, selectedAll } = payload;
    const basicParams = { fileFormat, compressionFormat, fileName };

    const params = selectedAll ? { ...basicParams, rule: getSuppersionContactsRule() } : { ...basicParams, emails };
    const count = selectedAll ? yield select(getTotalCount) : payload.emails.length;

    try {
        const resp = yield call(exportSuppressions, params);

        while (checkExportStatus) {
            yield delay(5000);
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }
        }

        window.open(resp.data.Link, '_blank');

        yield put(ExportSuppressionSuccess());
        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('suppressionsexported', { count }),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ExportSuppressionError());
        yield handleError(e);
    }
}

function* getPattern(action): any {
    const { payload } = action;

    yield put(GetContactsPatternStart());
    try {
        const resp = yield call(getContactsPattern, { fileName: payload.fileName });
        const { Pattern } = resp;
        yield put(GetContactsPatternSuccess(Pattern, payload.fileName));
    } catch (e) {
        Logger.error(e);
        yield put(GetContactsPatternError());
        yield put(
            ShowDialog({
                // severity: 'error',
                title: i18n.t('invalidfile'),
                body: payload.errorModal,
                cancelLabel: i18n.t('close'),
            })
        );
    }
}

function* deleteFileWithList(action): any {
    const { fileName } = action.payload;

    try {
        yield call(deleteFile, fileName);
    } catch (e) {
        yield handleError(e);
    }
}

function* uploadSuppressionsFile(action): any {
    const { file, errorModal } = action.payload;

    yield put(UploadFiles(file));
    yield take('@@upload/UPLOAD_SUCCESS');
    const uploadState = yield select(getUploadState);
    yield put(GetContactsPattern(uploadState?.fileuploaded?.FileName, errorModal));
}

function* watchUploadSuppressionsFile(): any {
    yield takeEvery(SuppressionsActionTypes.UPLOAD_FILES, uploadSuppressionsFile);
}

function* watchDeleteSuppressionsList(): any {
    yield takeEvery(SuppressionsActionTypes.DELETE_SUPPRESSION_LIST, deleteFileWithList);
}

function* watchGetPattern(): any {
    yield takeLatest(SuppressionsActionTypes.GET_CONTACTS_PATTERN, getPattern);
}

function* watchExportSuppressions(): any {
    yield takeLatest(SuppressionsActionTypes.EXPORT_SUPPRESSIONS, exportSuppression);
}

function* watchDeleteSuppressions(): any {
    yield takeEvery(SuppressionsActionTypes.DELETE_SUPPRESSION, deleteSuppression);
}

function* watchFindSuppressions(): any {
    yield takeLatest(SuppressionsActionTypes.SEARCH_FOR_CONTACT, loadContacts);
}

function* watchLoadContacts(): any {
    yield takeEvery(SuppressionsActionTypes.FETCH_CONTACTS, loadContacts);
}

function* watchCreateSuppression(): any {
    yield takeEvery(SuppressionsActionTypes.CREATE_SUPPRESSION, createSuppression);
}

function* contactsUnsubscribedOverviewSaga(action: GetContactsUnsubscribedOverviewAction): any {
    try {
        const { from, to } = action.payload;

        const response = yield call(getContactsUnsubscribedOverview, from, to);

        const overview = response.data;

        const unsubReasonsOrder = [
            UnsubReason.Unknown,
            UnsubReason.NoLongerWant,
            UnsubReason.NeverConsented,
            UnsubReason.IrrelevantContent,
            UnsubReason.TooFrequent,
            UnsubReason.DeceptiveContent,
        ];
        const unsubReasonsArray = overview.Reasons;
        const unsubReasons = [];

        forEach(unsubReasonsOrder, (el) => {
            const reason = find(unsubReasonsArray, (o) => {
                return o.Reason === el;
            });
            unsubReasons.push(reason);
        });

        const { unsubscribedContacts } = yield select((state): any => state.suppressions);

        const pagination: SuppressionsContactsUnsubscribedPaginationInterface = {
            ...unsubscribedContacts.pagination,
            ...(from && { from: dayjs(from).format(SHORT_DATE_FORMAT) }),
            ...(to && { to: dayjs(to).format(SHORT_DATE_FORMAT) }),
        };

        yield put(push({ search: stringify(pagination as any) }));

        const updatedUnsubscribedContacts = {
            ...unsubscribedContacts,
            overview,
            pagination,
            unsubReasons,
        };

        yield put(GetContactsUnsubscribedOverviewSuccess(updatedUnsubscribedContacts));
    } catch (e) {
        yield put(GetContactsUnsubscribedOverviewError());
        yield handleError(e);
    }
}

function* watchContactsUnsubscribedOverview(): any {
    yield takeLatest(SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED_OVERVIEW, contactsUnsubscribedOverviewSaga);
}

function* contactsUnsubscribedSaga(action: GetContactsUnsubscribedAction): any {
    try {
        const { from, to, limit = DEFAULT_PER_PAGE_COUNT, offset = 0, email, orderBy, statuses } = action.payload;

        const activePage = calculateActivePage(offset, limit);
        const response = yield call(getContactsUnsubscribed, { statuses, from, to, limit, offset, orderBy, email });

        const statusCount = extractUnsubscribedContactsStatusCounts(response.headers);
        const contactsList = response.data;
        const { unsubscribedContacts } = yield select((state): any => state.suppressions);

        const urlQueryString: SuppressionsContactsUnsubscribedPaginationInterface = {
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            to: dayjs(to).format(SHORT_DATE_FORMAT),
            ...(email ? { email } : {}),
            ...(statuses ? { statuses } : {}),
            perPage: limit,
            activePage,
            ...(orderBy ? { orderBy } : {}),
        };
        yield put(push({ search: stringify(urlQueryString as any) }));

        const updatedUnsubscribedContacts = {
            ...unsubscribedContacts,
            contactsList,
            pagination: { ...urlQueryString },
            statusCount,
        };

        yield put(GetContactsUnsubscribedSuccess(updatedUnsubscribedContacts));
    } catch (e) {
        yield put(GetContactsUnsubscribedError());
        yield handleError(e);
    }
}

function* watchContactsUnsubscribed(): any {
    yield takeLatest(SuppressionsActionTypes.GET_CONTACTS_UNSUBSCRIBED, contactsUnsubscribedSaga);
}

function* exportContactsUnsubscribedSaga(action: ExportContactsUnsubscribedAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    try {
        const resp = yield call(exportContactsUnsubscribed, payload);

        while (checkExportStatus) {
            yield delay(5000);
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);

            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }
        }

        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('contactsexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(push(ExportsPaths.Exports));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchExportContactsUnsubscribed(): any {
    yield takeLatest(SuppressionsActionTypes.EXPORT_CONTACTS_UNSUBSCRIBED, exportContactsUnsubscribedSaga);
}

const suppressionsSagas = [
    fork(watchDeleteSuppressions),
    fork(watchLoadContacts),
    fork(watchCreateSuppression),
    fork(watchFindSuppressions),
    fork(watchExportSuppressions),
    fork(watchGetPattern),
    fork(watchDeleteSuppressionsList),
    fork(watchUploadSuppressionsFile),
    fork(watchContactsUnsubscribedOverview),
    fork(watchContactsUnsubscribed),
    fork(watchExportContactsUnsubscribed),
];

export default suppressionsSagas;
